// src/services/subscriptionService.ts
import { AxiosResponse } from 'axios';
import apiClient from './apiClient';

export interface BillingPlanResponse {
  name: 'Basic' | 'Premium';
  stripe_price_id: string;
  unit_amount: number;
  block_size: number;
  block_price: number;
  trial_period_days: number;
  currency: string;
}

/** GET /users/subscription/plans/ */
export const getBillingPlans = (): Promise<
  AxiosResponse<BillingPlanResponse[]>
> => apiClient.get('/users/subscription/plans/');

export interface SubscriptionStatusResponse {
  subscription: {
    stripe_subscription_id: string;
    plan: {
      name: 'Basic' | 'Premium';
      stripe_price_id: string;
      block_size: number;
      block_price: number;
      trial_period_days: number;
    };
    total_blocks: number;
    block_count: number;
    usage_count: number;
    trial_ends_at: string | null;
    status: string;
    current_period_end: string;
    created_at: string;
    has_used_trial: boolean;
    upcoming_plan?: {
      name: 'Basic' | 'Premium';
      stripe_price_id: string;
      block_size: number;
      block_price: number;
      trial_period_days: number;
    };
    upcoming_block_count?: number;
    upcoming_effective_at?: string;
    /** キャンセル予約中かどうか */
    is_cancel_scheduled: boolean;
    /** キャンセル（または予約）された日付。予約中は nextUpdateDate、実キャンセル後は current_period_end */
    canceled_at: string | null;
    /** 操作可能かどうか（キャンセル予約中／実キャンセル後は false） */
    can_modify: boolean;
  } | null;
}

/** GET /users/subscription/status/ */
export const subscriptionStatus = (): Promise<
  AxiosResponse<SubscriptionStatusResponse>
> => apiClient.get('/users/subscription/status/');

export interface CreateSessionParams {
  plan_name: string; // "Basic" or "Premium"
  blocks: number; // 1 チャンク(500通) 単位
}
export interface CreateSessionResponse {
  checkout_url: string;
}

/** 新規契約 **/
/** POST /users/subscription/create-checkout-session/ */
export const createCheckoutSession = (
  params: CreateSessionParams
): Promise<AxiosResponse<CreateSessionResponse>> =>
  apiClient.post('/users/subscription/create-checkout-session/', params);

/** プラン変更 **/
export interface UpdateSubscriptionParams {
  mode: 'upgrade' | 'downgrade' | 'schedule_change' | 'cancel';
  plan_name?: string;
  blocks?: number;
}
export interface UpdateSubscriptionResponse {
  stripe_subscription_id: string;
  plan: string;
  blocks: number;
  status: string;
  current_period_end: string;
}
export const updateSubscription = (
  params: UpdateSubscriptionParams
): Promise<AxiosResponse<UpdateSubscriptionResponse>> =>
  apiClient.post('/users/subscription/change/', params);

/**
 * 月内限定の追加ブロック購入
 * POST /users/subscription/addon/
 */
export interface PurchaseAddonParams {
  blocks: number;
}
export interface PurchaseAddonResponse {
  id: number;
  blocks: number;
  expires_at: string;
  stripe_invoice_id: string;
  stripe_invoice_item_id: string;
  created_at: string;
}

export const purchaseAddon = (
  params: PurchaseAddonParams
): Promise<AxiosResponse<PurchaseAddonResponse>> =>
  apiClient.post('/users/subscription/addon/', params);

/**
 * Stripe Customer Portal を開くためのセッションを取得
 * POST /users/billing/portal/
 */
export interface BillingPortalResponse {
  url: string;
}
export const openBillingPortal = (): Promise<
  AxiosResponse<BillingPortalResponse>
> => apiClient.post('/users/billing/portal/');
